import Link from 'next/link';

import { Panel, PanelHeader } from './panel';
import type { ThreatMetric } from '@lib/page-utils/player-profile/metrics';
import { percentileFillClass } from '@lib/page-utils/player-profile/percentiles';
import {
  formatNumber,
  formatOrdinal
} from '@lib/page-utils/player-profile/format';

export type UnderlyingMetricRow = {
  metric: ThreatMetric;
  value: number | null;
  percentile: number | null;
};

type UnderlyingMetricsProps = {
  title: string;
  subtitle: string;
  rows: UnderlyingMetricRow[];
  decimals: number;
  optaHref: string;
};

const MetricRow = ({
  row,
  decimals
}: {
  row: UnderlyingMetricRow;
  decimals: number;
}) => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 border-b border-neutral-100 py-3.5 last:border-b-0 sm:grid-cols-[minmax(0,210px)_92px_minmax(0,1fr)_74px] sm:gap-x-[18px]">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-neutral-800">
        {row.metric.label}
      </p>
      {row.metric.note && (
        <p className="text-[11px] text-neutral-400">{row.metric.note}</p>
      )}
    </div>

    <p className="text-right font-sans text-[22px] font-bold tabular-nums leading-none text-neutral-900 sm:text-left">
      {formatNumber(row.value, decimals)}
    </p>

    <div
      className="col-span-2 h-2 rounded-full bg-neutral-100 sm:col-span-1"
      role="img"
      aria-label={
        row.percentile === null
          ? 'No percentile available'
          : `${formatOrdinal(row.percentile)} percentile`
      }
    >
      {row.percentile !== null && (
        <div
          className={`h-full rounded-full ${percentileFillClass(
            row.percentile
          )}`}
          style={{ width: `${row.percentile}%` }}
        />
      )}
    </div>

    <p className="hidden text-right text-[13px] font-bold text-neutral-700 sm:block">
      {formatOrdinal(row.percentile)}
    </p>
  </div>
);

export const UnderlyingMetrics = ({
  title,
  subtitle,
  rows,
  decimals,
  optaHref
}: UnderlyingMetricsProps) => (
  <Panel>
    <PanelHeader
      title={title}
      subtitle={subtitle}
      action={
        <Link
          href={optaHref}
          className="text-[13px] font-semibold text-primary-600 hover:underline"
        >
          Open in OPTA stats →
        </Link>
      }
    />
    <div className="border-t border-neutral-200 px-5 sm:px-6">
      {rows.map((row) => (
        <MetricRow key={row.metric.statPath} row={row} decimals={decimals} />
      ))}
    </div>
  </Panel>
);
